/**
 * Brand Configuration Registry
 *
 * This file defines the CareerOffensive brand configuration.
 * The multi-brand structure is kept for future extensibility.
 */

export type BrandId = 'careeroffensive';

export interface BrandConfig {
  id: BrandId;
  name: string;
  alias: string; // Used for env var prefixes (e.g., CO_)
  domain: string;
  enabled: boolean;
}

/**
 * Registry of all supported brands
 */
export const BRANDS: Record<BrandId, BrandConfig> = {
  careeroffensive: {
    id: 'careeroffensive',
    name: 'Career Offensive',
    alias: 'CO',
    domain: 'careeroffensive.com',
    enabled: true,
  },
};

/**
 * List of supported project IDs
 */
export const SUPPORTED_PROJECTS = Object.keys(BRANDS) as BrandId[];

/**
 * Map of project names to their aliases (for env var resolution)
 * Format: "projectId:ALIAS"
 */
export const SUPPORTED_PROJECTS_WITH_ALIASES = SUPPORTED_PROJECTS.reduce(
  (acc, projectId) => {
    acc[projectId] = BRANDS[projectId].alias;
    return acc;
  },
  {} as Record<BrandId, string>
);

/**
 * Default brand to use when none is specified
 */
export const DEFAULT_BRAND: BrandId = 'careeroffensive';

/**
 * Get brand config by ID
 */
export function getBrandById(brandId: string): BrandConfig | undefined {
  return BRANDS[brandId as BrandId];
}

/**
 * Get brand config by domain
 */
export function getBrandByDomain(domain: string): BrandConfig | undefined {
  return SUPPORTED_PROJECTS.map(id => BRANDS[id]).find(
    brand => brand.domain === domain || domain.includes(brand.domain)
  );
}

/**
 * Detect brand from hostname
 * In local development (localhost), falls back to environment variable
 */
export function detectBrandFromHostname(hostname: string): BrandId {
  // For local development (localhost), check environment variable
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    // Check both runtime env vars (works in middleware/server components)
    const envBrand = typeof process !== 'undefined'
      ? (process.env.NEXT_PUBLIC_BRAND || process.env.BRAND)
      : undefined;

    if (envBrand && BRANDS[envBrand as BrandId]) {
      return envBrand as BrandId;
    }
  }

  // Production: detect from hostname
  const brand = getBrandByDomain(hostname);
  return brand?.id || DEFAULT_BRAND;
}
